import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, FormControl, Spinner, Stack, Table } from 'react-bootstrap';
import { GET_EVENT_BY_POST_ID } from '../../graphql/event/queries';
import { Event } from '../../graphql/__generated__/graphql';
import { PayPalButton } from '../Form/PaymentButton/PayPalButton';
import { useAuth } from 'react-oidc-context';

interface EventDetailsProps {
    postId: string;
    guest?: boolean;
}

const EventDetails: React.FC<EventDetailsProps> = ({ postId, guest }) => {
    const auth = useAuth();
    const { data, loading, error } = useQuery(GET_EVENT_BY_POST_ID, {
        variables: { id: postId },
    });

    const [event, setEvent] = useState<Event>();
    const [expired, setExpired] = useState<boolean>();
    const [soldOut, setSoldOut] = useState<boolean>();
    const [cart, setCart] = useState<any>(null);
    const [complete, setComplete] = useState(false);
    const [guestEmail, setGuestEmail] = useState('');
    const [registrationError, setRegistrationError] = useState<string>();
    const [registering, setRegistering] = useState(false);

    const handleExit = (error: object | null, metadata: object) => {
        console.log('Exit', error, metadata);
    };

    const getPrice = () => (guest ? event?.nonMemberPrice ?? 0 : event?.memberPrice ?? 0);
    const isFree = getPrice() === 0;

    const registerForFreeEvent = async () => {
        if (guest && !guestEmail.trim()) {
            setRegistrationError('Enter an email address to register as a guest.');
            return;
        }

        setRegistering(true);
        setRegistrationError(undefined);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_ORDERAPI_ENDPOINT ?? ''}/free-event`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: auth.user?.access_token
                            ? `Bearer ${auth.user.access_token}`
                            : '',
                    },
                    body: JSON.stringify({
                        eventId: event?.eventId,
                        emailAddress: guest ? guestEmail.trim() : undefined,
                    }),
                }
            );

            if (!response.ok) {
                const error = await response.text();
                throw new Error(error || 'Registration could not be completed.');
            }

            setComplete(true);
        } catch (error) {
            setRegistrationError(
                error instanceof Error ? error.message : 'Registration could not be completed.'
            );
        } finally {
            setRegistering(false);
        }
    };

    useEffect(() => {
        if (data && data.eventByPostId) {
            const event = data.eventByPostId;

            if (event.soldOut === true) {
                setSoldOut(true);
            } else {
                const eventEndDate = new Date(event.endDate);
                setExpired(eventEndDate < new Date());
            }
            setEvent(data.eventByPostId);
            const newCart = {
                items: [
                    {
                        id: data.eventByPostId?.eventId,
                        quantity: '1',
                        type: 'event',
                    },
                ],
            };
            setCart(newCart);
        }
    }, [loading, data, error]);

    return loading ? (
        <Spinner />
    ) : (
        <div>
            <h2 className="mb-4">{event?.name}</h2>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Stack direction="horizontal" gap={4} className="mb-3">
                    <span>
                        <Badge pill className="me-1 p-2">
                            <i className="bi bi-calendar "></i>
                        </Badge>
                        {new Date(event?.startDate).toLocaleDateString('en-gb')}
                    </span>
                    <span>
                        <Badge pill className="me-1 p-2">
                            <i className="bi bi-clock"></i>
                        </Badge>
                        {new Date(event?.startDate).toLocaleTimeString('en-gb')}
                    </span>
                </Stack>
                <span>
                    <Button
                        size="sm"
                        style={{ borderRadius: '99px' }}
                        variant="outline-primary"
                        as={'a'}
                        href={event?.eventPostHref ?? '#'}
                        className="me-1"
                    >
                        <i className="bi bi-info-circle me-2"></i>
                        <small>about this event</small>
                    </Button>
                </span>
            </div>
            <Alert variant="light" className="text-primary">
                <Table>
                    <thead>
                        <tr>
                            <th></th>
                            <th>Price</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td>General Admission</td>
                            <td>&pound; {getPrice().toFixed(2)}</td>
                            <td>&pound; {getPrice().toFixed(2)}</td>
                        </tr>
                    </tbody>
                </Table>

                {complete && (
                    <h2 className="my-4">
                        <i className="bi bi-check-lg me-2"></i>
                        {isFree ? 'Registration Complete' : 'Payment Complete'}
                    </h2>
                )}

                <p>
                    You have {complete ? <b>successfully registered</b> : 'requested'} one attendee
                    place for this event.
                    {complete
                        ? ' You will receive an email to confirm your booking.'
                        : ''}
                </p>
            </Alert>

            {soldOut ? (
                <p>There are no more spaces available for this event.</p>
            ) : expired ? (
                <p>This event has now finished.</p>
            ) : complete ? (
                <div className="w-100 text-center">
                    {guest ? (
                        <b className="text-info">You may now close this page.</b>
                    ) : (
                        <Button as={'a'} href="/dashboard" size="lg">
                            Go To Dashboard
                        </Button>
                    )}
                </div>
            ) : (
                isFree ? (
                    <div className="d-grid">
                        {guest && (
                            <FormControl
                                className="mb-3"
                                type="email"
                                value={guestEmail}
                                onChange={(e) => setGuestEmail(e.target.value)}
                                placeholder="Email address"
                                aria-label="Email address"
                            />
                        )}
                        {registrationError && <Alert variant="danger">{registrationError}</Alert>}
                        <Button
                            variant="primary"
                            onClick={registerForFreeEvent}
                            disabled={registering}
                        >
                            {registering ? 'Registering...' : 'Register for free'}
                        </Button>
                    </div>
                ) : (
                    cart?.items && (
                    <div className="d-grid">
                        <PayPalButton
                            disabled={false}
                            cart={cart}
                            environment={'sandbox'}
                            onExit={handleExit}
                            onSuccess={() => setComplete(true)}
                            label={
                                <>
                                    {' '}
                                    <i className="bi bi-basket me-2"></i>Pay{' '}
                                    <b className="ms-1">
                                        <small>
                                            (&pound; {getPrice().toFixed(2)})
                                        </small>
                                    </b>
                                </>
                            }
                        />
                    </div>
                    )
                )
            )}
        </div>
    );
};

export default EventDetails;
